/**
 *
 * (HTL 22-05-23)
 * This file fetches Facebook Ads Library page as HTML, extracts the data we need and returns it.
 *
 */

// TYPES
import { SocialExtensionResult } from "../types";
// EXTERNAL
import axios from "axios";
// CONFIG
import config from "../../../../configs/config";
// HELPERS
import get_current_session from "../../../../helpers/get_current_session";

export async function fetchAdsLibraryPageData(url: string): Promise<SocialExtensionResult> {
  try {
    /**
     * MAKE REQUEST FOR HTML
     * Can I make the request without using a server function?
     */
    const htmlContent: { data: { payload: string } } = await axios.get(`${config.apiGateway.URL}/get-html?url=${url}`, {
      headers: {
        Authorization: `Bearer ${await get_current_session()}`,
      },
    });
    /**
     * EXTRACT DATA
     */
    const data: SocialExtensionResult = extractData(htmlContent.data.payload, url);
    return data;
  } catch (error) {
    /**
     * JA (26-09-23)
     * Error handling for input and payload responses.
     */
    switch (error.response?.data.payload) {
      case "Error on input":
        throw Error("Error on input", { cause: "Please add a Meta Ads Library URL." });
      case "Platform not supported":
        throw Error("Platform not supported", { cause: "Only Meta's Ads Library is supported." });
      case "Link type not supported":
        throw Error("Link type not supported", { cause: "Only Meta's Ads Library is supported." });
      case "Invalid URL":
        throw Error("Invalid URL", { cause: "Only Meta's Ads Library is supported." });
      case "This ad is no longer available":
        throw Error("This ad is no longer available", { cause: "Please use a different link." });
      default:
        throw Error("Social fetch is currently unavailable", { cause: "Please try again in 2 minutes." });
    }
  }
}

export function extractData(pageSource: string, url: string): SocialExtensionResult {
  const split1 = pageSource.split(`"deeplinkAdCard\":`)[1];
  const split2 = split1.split(`,\"deeplinkNoResultReason`)[0];
  /**
   * Extract data
   */
  const adProps = JSON.parse(split2);
  /**
   * HTL (17-05-23)
   * Backup content refers to content that exists on the Ads Library post, but isn't found in the snapshot. We have to search through another data object to get this, using the ID that should exist in the snapshot object.
   * Markup content refers to content that exists on some posts. This content doesn't exist within the "deeplinkadcard".
   */
  const bodyContentId = adProps?.body?.__m;
  const bodyContent = bodyContentId ? JSON.parse(pageSource.split(`${bodyContentId}`)[1]?.split(",")[1]?.split(",1,")[0])?.__html : null;
  const markupContentArr = "[[" + pageSource.split('"markup":[[')[1].split(',{"__html":"Saved searches')[0] + "]]";
  const markupContent = JSON.parse(markupContentArr)[0][1]["__html"] || null;
  const backupContent = bodyContent ? bodyContent : markupContent ? markupContent : "";

  const snapshot = adProps?.snapshot;
  /**
   * HTL (08-06-23)
   * Determine if there are multiple versions using "snapshot?.dynamic_item_flags" property.
   * If an ad has multiple versions, "dynamic_item_flags" = "dynamic_item_flags":{"0":[],"1":[],"2":[],"3":[],"4":[],"5":[]}.
   * If an ad has one version, "dynamic_item_flags" = "dynamic_item_flags":{}
   * Check to see if display_format is "dco" (dynamic creative optimization) as the dynamic_item_flags property is sometimes not used.
   */
  let adHasVersions: boolean = Object.keys(snapshot.dynamic_item_flags).length > 0 || snapshot.display_format === "dco" ? true : false;
  let result: SocialExtensionResult = {
    brand: {
      username: snapshot?.page_name || "",
      linkToPost: url,
      profilePictureUrl: snapshot?.page_profile_picture_url || "",
      /**
       * HTL (17-05-23)
       * Gets markup if the ad doesn't have 'cards'. If there are cards, get the first card's ad copy. Fallback = empty string.
       */
      postDescription: adHasVersions ? snapshot?.cards[0]?.body : snapshot?.body?.markup?.__html || backupContent || "", //|| snapshot?.cards[0]?.body
    },
    cta: {
      text: snapshot?.cta_text || snapshot?.cards[0]?.cta_text || "",
      link: snapshot?.link_url || "",
      title: snapshot?.title || "",
    },
    media: {
      type: snapshot?.display_format || "",
      /**
       * AF (17-08-23)
       * If the ad has a video asset in frame 1 we want to store a poster image to help with accessing dimensions later on
       * this ternary statement checks if display_format is video or if there is a video url present in frame 1 (some video ads have display_type: dpa)
       * if true, it checks for the location of video_preview_image_url and returns it
       * if MAL link is not a video ad then return ""
       */
      poster: snapshot?.display_format === "video" || snapshot?.cards[0]?.video_hd_url || snapshot?.cards[0]?.video_sd_url ? (snapshot?.cards.length ? snapshot?.cards[0].video_preview_image_url : snapshot?.videos[0].video_preview_image_url) : "",
    },
    /**
     * HTL (09-06-23)
     * If the ad has different versions, take the first frame only, all other versions will be represented as separate frames, we don't want all other versions.
     * Maps through cards, if they exist. Some Ads Library ads don't have 'cards', but will have an array of videos in snapshot.videos.
     * If there are no cards, we map through the videos and return the URL for the standard definition version.
     * We don't currently (12-06-23) support a user to choose which version they want to scrape, as there's no change in the URL. Solution = before the assets are scraped, a popup shows all variants, and user chooses which one(s) they want.
     */
    /* Ad Versions*/
    frames: adHasVersions
      ? [
          {
            type: snapshot?.cards[0].original_image_url ? "image" : "video",
            url: snapshot?.cards[0].original_image_url || snapshot?.cards[0].video_hd_url || snapshot?.cards[0].video_sd_url || "",
            title: snapshot?.cards[0].title || "",
          },
        ]
      : /* Cards */
      snapshot?.cards.length
      ? snapshot.cards.map((x: any) => ({
          type: x.original_image_url ? "image" : "video",
          url: x.original_image_url || x.video_hd_url || x.video_sd_url || "",
          title: x.title || "",
        }))
      : /* Snapshot Images */
      snapshot?.images.length
      ? snapshot.images.map((y: any) => ({
          type: "image",
          url: y.original_image_url,
          title: y.title || "",
        }))
      : /* Snapshot Videos */
      snapshot?.videos.length
      ? snapshot.videos.map((z: any) => ({
          type: "video",
          url: z.video_hd_url || z.video_sd_url || "",
          title: z.title || "",
        }))
      : /* Fallback */
        [],
  };
  return result;
}
